import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useGetClaimPrintPreviewMutation, useLazyGetClaimPrintInputsQuery } from '@/store/api/saleControlApi'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: Outbound Logistics "Claim" print. frmCtrlSalesQryClaim_Choose collects
// two free-text inputs (Claimable At / Notion) BEFORE printing, then opens
// rpCtrlSalesClaimCheckWeight (Weight claims) or rpCtrlSalesClaimCheckOthers
// (Others / Quality claims) — the BE resolves the branch from the row
// claim_type. This dialog is the chooser + preview in one: two TextFields + a
// "Print Preview" button → useGetClaimPrintPreviewMutation → iframe srcDoc
// preview + Print (hidden when !canEdit) → printAndUploadReport. Responsive A4
// fit + mobile-stacked inputs (skill /responsive-style + /convert-pr).

interface ClaimPrintDialogProps {
  open: boolean
  claimId: number | null
  claimScsCode: string | null
  invoiceNo?: string | null
  claimType?: string | null
  contractRef?: string | null
  canEdit?: boolean
  onClose: () => void
}

// Readonly (Access Enabled=NotDefault) — nền xám phân biệt với field nhập được.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: (theme: { palette: { text: { secondary: string } } }) =>
      theme.palette.text.secondary,
    fontWeight: 600,
  },
} as const

export default function ClaimPrintDialog({
  open,
  claimId,
  claimScsCode,
  invoiceNo = null,
  claimType = null,
  contractRef = null,
  canEdit = true,
  onClose,
}: ClaimPrintDialogProps) {
  const [claimableAt, setClaimableAt] = useState('')
  const [notion, setNotion] = useState('')
  const [toastMessage, setToastMessage] = useState('')
  const [getPreview, { data, isLoading, reset }] = useGetClaimPrintPreviewMutation()
  const [fetchInputs] = useLazyGetClaimPrintInputsQuery()

  const iframeTitle = 'Claim Print Preview'
  const html = data?.html ?? ''
  const previewHtml = html ? buildPrintPreviewHtml(html, { styleId: 'claim-print-preview-overrides' }) : ''

  // Init CHỈ 1 lần mỗi lần mở (theo claimId): reset preview + pre-fill Claimable
  // at / Notion đã lưu (Access DLookUp public_claim_scs). Dùng ref guard vì RTK
  // đổi identity của `reset` (deps chứa promise/requestId) MỖI lần mutation chạy →
  // nếu effect re-run khi bấm Print Preview, nó gọi lại `reset()` xoá kết quả
  // preview → form Print không mở. Guard chặn re-run đó.
  const initedClaimRef = useRef<number | null | undefined>(undefined)
  useEffect(() => {
    if (!open) {
      initedClaimRef.current = undefined
      return
    }
    if (initedClaimRef.current === claimId) return
    initedClaimRef.current = claimId
    setClaimableAt('')
    setNotion('')
    reset()
    if (claimId == null) return
    let cancelled = false
    void fetchInputs(claimId)
      .unwrap()
      .then((res) => {
        if (cancelled) return
        setClaimableAt(res.claimableAt ?? '')
        setNotion(res.notion ?? '')
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [open, claimId, reset, fetchInputs])

  const handlePreview = () => {
    if (claimId == null) return
    void getPreview({ claimId, claimableAt, notion })
      .unwrap()
      .catch((err) => {
        const detail = err?.data?.detail
        setToastMessage(typeof detail === 'string' ? detail : 'Cannot generate print preview.')
      })
  }

  const title = `Claim${claimScsCode ? ` (${claimScsCode})` : ''}`

  return (
    <>
      {open && isLoading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}

      {/* Phase 1 — chooser (Claimable At / Notion) before printing. */}
      <Dialog open={open && !html} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            {title}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* frmCtrlSalesQryClaim_Choose — 1 cột dọc: header + 4 field readonly
              (Claim ID / Claim Code / Invoice No / Claim Type) + 2 field nhập
              (Claimable at / Notion, multiline). Thứ tự theo Top của Access. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              Input Value for Print Form
            </Box>
            <TextField
              label="Claim ID"
              value={claimId ?? ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: -1 }}
              sx={readonlyFieldSx}
            />
            <TextField
              label="Claim Code"
              value={claimScsCode ?? ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: -1 }}
              sx={readonlyFieldSx}
            />
            <TextField
              label="Invoice No"
              value={invoiceNo ?? ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: -1 }}
              sx={readonlyFieldSx}
            />
            <TextField
              label="Claim Type"
              value={claimType ?? ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: -1 }}
              sx={readonlyFieldSx}
            />
            <TextField
              label="Claimable at"
              value={claimableAt}
              onChange={(e) => setClaimableAt(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={3}
              inputProps={{ tabIndex: 1 }}
            />
            <TextField
              label="Notion"
              value={notion}
              onChange={(e) => setNotion(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={3}
              inputProps={{ tabIndex: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose} size="small">
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={claimId == null || isLoading}
            onClick={handlePreview}
          >
            Print Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase 2 — A4 preview iframe + Print. */}
      <Dialog
        open={open && Boolean(html)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            {title}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => reset()} size="small">
              Back
            </Button>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!html}
                onClick={() => {
                  // Tên file PDF = <Contract ref.>_<Claim no>_<Type of claim>.pdf
                  // (bỏ phần rỗng để tránh "__"). Chị Đẹp chốt 2026-07-18.
                  const namePart = [contractRef, claimScsCode, claimType]
                    .map((p) => sanitizePdfFileNamePart(p || ''))
                    .filter(Boolean)
                    .join('_')
                  const printTitle = `${namePart || 'claim'}.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesClaim',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
